import { useMemo } from "react";

const calculateDifference = (currentValue, fixedValue, hasPercentageSuffix) => {
  if (isNaN(currentValue) || isNaN(fixedValue)) return 0;

  if (hasPercentageSuffix) {
    return currentValue - fixedValue;
  } else {
    if (fixedValue !== 0) {
      return ((currentValue - fixedValue) / Math.abs(fixedValue)) * 100;
    }
  }
  return 0;
};

// Modified function to remove + or - signs
const formatDifference = (difference) => {
  return `${Math.abs(difference).toFixed(1)}%`; // Using Math.abs to remove signs
};

const getComparisonStyle = (difference) => {
  if (difference > 0) {
    return { icon: "icon-comparison-up", color: "#4CAF50" };
  } else if (difference < 0) {
    return { icon: "icon-comparison-down", color: "#F44336" };
  } else {
    return { icon: "icon-comparison-equal", color: "#FFC107" };
  }
};

export const useKpiComparison = (
  metadata,
  value,
  staticSuffix,
  data,
  auxComparisonData,
  staticPrefix
) => {
  return useMemo(() => {
    const comparison = metadata?.kpiPanelComparison?.[0];

    if (!comparison) {
      return null;
    }

    const currentValue = parseFloat(value);
    if (isNaN(currentValue)) {
      return null;
    }

    const comparisonLabel = comparison.label || "";
    const hasPercentageSuffix = staticSuffix === "%";

    // CASE 1: VALUE + FIXED - Comparison with fixed value
    if (
      comparison?.comparisonDomain === "VALUE" &&
      comparison?.valueMode === "FIXED" &&
      comparison?.fixedValue
    ) {
      const fixedValue = parseFloat(comparison.fixedValue);

      if (isNaN(fixedValue)) {
        return null;
      }

      const difference = calculateDifference(
        currentValue,
        fixedValue,
        hasPercentageSuffix
      );
      const style = getComparisonStyle(difference);

      return {
        label: comparisonLabel,
        difference: formatDifference(difference, hasPercentageSuffix),
        valueToCompare: staticPrefix + fixedValue +staticSuffix,
        icon: style.icon,
        color: style.color,
        rawDifference: difference,
        type: "VALUE_FIXED",
        showPercentage: comparison?.showPercentage,
      };
    }

    // CASE 2: VALUE + COLUMN - Comparison with another column
    if (
      comparison?.comparisonDomain === "VALUE" &&
      comparison?.valueMode === "COLUMN" &&
      comparison?.compareToColumn
    ) {
      if (!data[0][comparison.compareToColumn]) return;

      const comparisonValue = parseFloat(data[0][comparison.compareToColumn]);

      if (isNaN(comparisonValue)) return null;

      const difference = calculateDifference(
        currentValue,
        comparisonValue,
        hasPercentageSuffix
      );

      const style = getComparisonStyle(difference);

      return {
        label: comparisonLabel,
        difference: formatDifference(difference, hasPercentageSuffix),
        valueToCompare: comparisonValue,
        icon: style.icon,
        color: style.color,
        rawDifference: difference,
        comparisonValue: comparisonValue,
        type: "VALUE_COLUMN",
        showPercentage: comparison?.showPercentage,
      };
    }

    // CASE 3: TIME - Temporal comparison
    if (
      comparison?.comparisonDomain === "TIME" &&
      comparison?.valueColumn &&
      auxComparisonData
    ) {
      if (!auxComparisonData[0][comparison?.valueColumn]) return;
      const comparisonValue = parseFloat(
        auxComparisonData[0][comparison.valueColumn]
      );
      if (isNaN(comparisonValue)) return null;

      const difference = calculateDifference(
        currentValue,
        comparisonValue,
        hasPercentageSuffix
      );
      const style = getComparisonStyle(difference);
      return {
        label: comparisonLabel,
        difference: formatDifference(difference, hasPercentageSuffix),
        valueToCompare: comparisonValue,
        icon: style.icon,
        color: style.color,
        rawDifference: difference,
        comparisonValue: comparisonValue,
        type: "TIME",
        showPercentage: comparison?.showPercentage,
      };
    }

    // If it doesn't match any case
    return null;
  }, [metadata?.kpiPanelComparison, value, staticSuffix, data]);
};
